import type { IRunPlan } from '../types';
import { writeJSON } from '../services/json';
import { writeDictionary } from '../services/loop';

/**
 * support writing all keys into the `<head>` tag of Scaffold Index
 */
export class HeadWriter {
  static readonly defaultMetaTags = [
    'charset="utf-8"',
    'http-equiv="content-script-type" content="text/javascript"',
    'http-equiv="content-style-type" content="text/css"',
    'http-equiv="content-type" content="text/html"',
    'http-equiv="window-target" content="_top"',
    'http-equiv="cache-control" content="no-cache, no-store, must-revalidate"',
    'http-equiv="pragma" content="no-cache"',
    'http-equiv="expires" content="0"',
    'name="copyright" content="Copyright (c) 2024 EAB"',
    // 'name="date" content="${settings.buildDate}"',
    'name="publisher" content="EAB Global Inc"',
    'name="designer" content="EAB Global Inc"',
    'name="author" content="EAB Global Inc"',
    'name="language" content="english"',
    'name="viewport" content="width=device-width, user-scalable=yes, initial-scale=1"',
    'name="googlebot" content="noarchive, noindex, nofollow"',
    'name="robots" content="noarchive, noindex, nofollow"',
    'name="distribution" content="web"',
    'name="rating" content="general"'
  ];

  /**
   * write all meta tags for the `<head>`
   */
  static writeMetaTagsCode(runPlan: IRunPlan) {
    return ([] as string[])
      .concat(this.defaultMetaTags)
      .concat(runPlan.scaffold.meta.included_tags)
      .map((meta) => `<meta ${meta} />`)
      .join('\n');
  }

  /**
   * write the title or `Remote View` to the `<title>` tag
   */
  static writeTitleCode(runPlan: IRunPlan) {
    return `<title>${runPlan.remote_view.title ?? 'Remote View'}</title>`;
  }

  /**
   * run design rules for A11y Buddy and FOUC
   */
  static writeStyleBlock(supports: { a11y_buddy: boolean; fouc: boolean }) {
    return `<style>
      ${
        !supports.a11y_buddy
          ? ''
          : `xi-a11y-buddy {
            --a11y-z-index: 888888;
          }`
      }

      ${
        !supports.fouc
          ? ''
          : `[no-fouc]:not([x-hip]) {
            display: none !important;
          }`
      }
    </style>`;
  }

  /**
   * write script to load HI/F
   */
  static writeFrameworkScript(runPlan: IRunPlan) {
    const hif = runPlan.hif;

    if (!hif.is_active) {
      return '';
    }

    return `<script
      src="https://cdn.eab.com/hif/${hif.version}/index.js"
      logging="${hif.logging.join(',')}"
      release="${hif.release}"
      ${hif.auto_start ? `auto-start` : ''}
      ${hif.attributes.join('\n')}
      ${hif.systems.map((system) => `system-load-${system}`).join('\n')}
      ${this.__writeCrateInstructions(hif.included_crates)}
      ${this.__writeCrateInstructions(hif.loaded_crates)}
    ></script>
    `;
  }

  /**
   * sent all HI/F settings to the `$ip.settings` command
   */
  static writeFrameworkFlags(runPlan: IRunPlan) {
    return `<script>
        $ip.settings(${writeJSON(runPlan.hif.settings)});
      </script>
      `;
  }

  /**
   * this is to address some micro and hosting applications which use `HIL`
   */
  static writeGlobalServices(runPlan: IRunPlan) {
    return `<script>
      $ip.global('@eab-aip/ai-transpiler', 'HIL');
      </script>`;
  }

  /**
   * write crate loading rules
   */
  private static __writeCrateInstructions(crates: Record<string, string>) {
    return writeDictionary(
      crates,
      (version, crate) => `crate-include-${crate.replace('@eab-', '').replace(/\//g, '-')}="${version}"`
    );
  }
}
